<template>
  <div class="etalon-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$T = 1 / [1 + F sin²(δ/2)]$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <g transform="translate(0, 10)">
            <!-- External Input beam -->
            <path d="M 0,100 L 150,100" stroke="#fcd34d" stroke-width="4" fill="none" class="glow-beam" />
            <!-- Text label -->
            <text x="20" y="90" fill="#fcd34d" font-size="10">入射光束</text>

            <!-- Cavity Mirrors -->
            <rect x="150" y="40" width="8" height="120" fill="rgba(255,255,255,0.2)" stroke="#94a3b8" />
            <rect :x="150 + gap" y="40" width="8" height="120" fill="rgba(255,255,255,0.2)" stroke="#94a3b8" />

            <!-- Animated Multibeam Reflections -->
            <g v-for="i in 5" :key="'bounce'+i">
               <line :x1="150" :y1="100" :x2="150+gap" :y2="100" stroke="#fcd34d" :stroke-width="3" :opacity="Math.pow(reflectivity, i-1)" />
            </g>

            <!-- Transmitted Output beam -->
            <path :d="`M ${150+gap+8},100 L 400,100`" stroke="#fcd34d" :stroke-width="4 * transmission" fill="none" class="glow-beam" :opacity="transmission" />
            <text :x="150+gap+20" y="90" fill="#fcd34d" font-size="10" :opacity="transmission">透射光 (干涉叠加)</text>

            <!-- Resonant Spectrum Plot -->
            <rect x="250" y="140" width="130" height="50" fill="#0f172a" stroke="#334155" rx="4" />
            <path :d="spectrumPath" fill="none" stroke="#2dd4bf" stroke-width="2" />
            <line :x1="250 + (gap-20)/80 * 130" y1="140" :x2="250 + (gap-20)/80 * 130" y2="190" stroke="#ef4444" stroke-width="1.5" stroke-dasharray="2,2" />
            <text x="255" y="150" fill="#94a3b8" font-size="8">透过率随腔长变化曲线</text>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>实时透射率 T</label>
              <span class="val">{{ (transmission * 100).toFixed(1) }} <small>%</small></span>
           </div>
           <div class="hud-item orange">
              <label>腔内干涉状态</label>
              <span class="val" :style="{color: transmission > 0.8 ? '#22c55e' : '#f59e0b'}">
                 {{ transmission > 0.8 ? '相长共振 (亮)' : '相消失谐 (暗)' }}
              </span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节腔长 (纳米级位移模拟)</label>
                    <el-slider v-model="gap" :min="20" :max="100" :step="0.5" size="small"></el-slider>
                 </div>
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">镜面反射率 R</label>
                    <el-slider v-model="reflectivity" :min="0.5" :max="0.95" :step="0.05" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>法布里-珀罗标准具利用两块高反射率平行的反射镜组成共振腔。光线在腔内多次反射形成<strong>多光束干涉</strong>。只有当腔长 <code>L</code> 是半波长的整数倍时，各次反射光的相位差 <code>δ = 2π(2L)/λ</code> 恰好为 <code>2π</code> 的整数倍，产生完全的<strong>相长干涉</strong>，透射率 <code>T</code> 达到峰值 100%。反射率 <code>R</code> 越高，精细度（Finesse）越大，透射峰越锐利。此原理是激光谐振腔和密集波分复用(DWDM)的核心。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EtalonViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      gap: 50,
      reflectivity: 0.8,
      wavelength: 20 // 相对单位波长
    }
  },
  computed: {
    transmission() {
       const delta = (2 * Math.PI * this.gap * 2) / this.wavelength;
       const F = (4 * this.reflectivity) / Math.pow(1 - this.reflectivity, 2);
       return 1 / (1 + F * Math.pow(Math.sin(delta/2), 2));
    },
    spectrumPath() {
       const points = [];
       const F = (4 * this.reflectivity) / Math.pow(1 - this.reflectivity, 2);
       // X maps gap from 20 to 100, mapping to 0-130 range
       for (let i = 0; i <= 130; i++) {
          const g = 20 + (i / 130) * 80;
          const delta = (2 * Math.PI * g * 2) / this.wavelength;
          const t = 1 / (1 + F * Math.pow(Math.sin(delta/2), 2));
          if (!isNaN(t)) {
             points.push(`${250 + i},${(185 - t * 40).toFixed(1)}`);
          }
       }
       return points.length > 0 ? "M " + points.join(" L ") : "";
    }
  }
}
</script>

<style scoped lang="scss">
.etalon-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
.glow-beam { filter: drop-shadow(0 0 4px rgba(252, 211, 77, 0.6)); }
</style>
